import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { 
  ArrowLeft, 
  Mail, 
  Phone, 
  Building2, 
  Briefcase, 
  Calendar,
  User,
  Globe,
  MapPin,
  Edit,
  Trash2,
  RefreshCw,
  Sparkles,
  Copy,
  CheckCircle2,
  XCircle,
  Clock,
  Bot,
  ExternalLink,
  Linkedin,
  Users,
  DollarSign,
  Target,
  TrendingUp,
  Award,
  Lightbulb,
  Network,
  Code,
  Newspaper,
  CheckSquare,
  MessageSquare,
  Twitter,
  Info,
  ChevronDown,
  ChevronUp
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { toast } from "sonner";
import EditLeadDialog from "./EditLeadDialog";
import TasksListSection from "./TasksListSection";
import ActivityLogsSection from "./ActivityLogsSection";
import FileUploadSection from "./FileUploadSection";
import LeadNotesEditor from "./LeadNotesEditor";
import DuplicateLeadsDialog from "./DuplicateLeadsDialog";
import SendEmailDialog from "./SendEmailDialog";
import IntroEmailComposer from "./IntroEmailComposer";

const API = (process.env.REACT_APP_BACKEND_URL || "http://localhost:8001") + "/api";

const LeadDetailsNew = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDuplicatesDialog, setShowDuplicatesDialog] = useState(false);
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [taskRefresh, setTaskRefresh] = useState(0);
  const [showProvenance, setShowProvenance] = useState(false);
  const [tailoredInsights, setTailoredInsights] = useState(null);
  const [loadingInsights, setLoadingInsights] = useState(false);
  const [showIntroEmailDialog, setShowIntroEmailDialog] = useState(false);

  // Prevent auto-scroll to bottom on page load
  useEffect(() => {
    // Ensure page starts at top
    window.scrollTo(0, 0);
    
    // Prevent any delayed scroll attempts
    const preventScrollTimer = setTimeout(() => {
      window.scrollTo(0, 0);
    }, 100);
    
    return () => clearTimeout(preventScrollTimer);
  }, []); // Run only once on mount

  useEffect(() => {
    fetchLeadDetails();
  }, [id]);

  const fetchLeadDetails = async () => {
    try {
      const response = await axios.get(`${API}/leads/${id}`, {
        withCredentials: true
      });
      setLead(response.data);
      setLoading(false);
    } catch (error) {
      toast.error("Failed to fetch lead details");
      setLoading(false);
    }
  };

  const triggerEnrichment = async () => {
    try {
      await axios.post(`${API}/leads/${id}/enrich`, {}, { withCredentials: true });
      toast.success("Enrichment started!");
      setTimeout(fetchLeadDetails, 2000);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to start enrichment");
    }
  };

  const generateTailoredInsights = async () => {
    setLoadingInsights(true);
    try {
      const response = await axios.post(
        `${API}/leads/${id}/generate-tailored-insights`,
        {},
        { withCredentials: true }
      );
      setTailoredInsights(response.data.insights);
      toast.success("Tailored insights generated!");
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Failed to generate insights";
      toast.error(errorMsg);
      if (errorMsg.includes("company profile not configured")) {
        toast.info("Please set up your company profile in Admin Panel first");
      }
    } finally {
      setLoadingInsights(false);
    }
  };

  const triggerReEnrichment = async () => {
    try {
      const response = await axios.post(`${API}/leads/${id}/re-enrich`, {}, { withCredentials: true });
      toast.success(response.data.message || "Re-enrichment started!");
      setTimeout(fetchLeadDetails, 2000);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to start re-enrichment");
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Are you sure you want to delete this lead?")) return;
    try {
      await axios.delete(`${API}/leads/${id}`, { withCredentials: true });
      toast.success("Lead deleted successfully");
      navigate("/");
    } catch (error) {
      toast.error("Failed to delete lead");
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard!`);
  };

  const getVerificationIcon = (status) => {
    switch (status) {
      case 'valid': return <CheckCircle2 className="w-4 h-4 text-green-600" />;
      case 'invalid': return <XCircle className="w-4 h-4 text-red-600" />;
      case 'risky': return <Clock className="w-4 h-4 text-amber-600" />;
      default: return <Clock className="w-4 h-4 text-slate-400" />;
    }
  };

  const isAIPopulated = (fieldName) => {
    return lead?.ai_populated_fields && lead.ai_populated_fields[fieldName];
  };

  const getAIPopulatedDate = (fieldName) => {
    if (lead?.ai_populated_fields && lead.ai_populated_fields[fieldName]) {
      const date = new Date(lead.ai_populated_fields[fieldName].populated_at);
      return date.toLocaleString();
    }
    return null;
  };

  if (loading || !lead) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  const enrichedData = lead.enriched_data || {};

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50 to-blue-50 p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <Button variant="ghost" onClick={() => navigate("/")}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Leads
          </Button>
          
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setShowDuplicatesDialog(true)}>
              <Copy className="w-4 h-4 mr-2" />
              Find Duplicates
            </Button>
            {(lead.email || lead.raw_data?.email) && (
              <>
                <Button variant="outline" onClick={() => setShowEmailDialog(true)}>
                  <Mail className="w-4 h-4 mr-2" />
                  Send Email
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => setShowIntroEmailDialog(true)}
                  className="bg-gradient-to-r from-purple-50 to-indigo-50 border-purple-300 hover:from-purple-100 hover:to-indigo-100"
                >
                  <Sparkles className="w-4 h-4 mr-2 text-purple-600" />
                  AI Intro Email
                </Button>
              </>
            )}
            <Button variant="outline" onClick={() => setShowEditDialog(true)}>
              <Edit className="w-4 h-4 mr-2" />
              Edit
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              <Trash2 className="w-4 h-4 mr-2" />
              Delete
            </Button>
          </div>
        </div>

        {/* Unified Lead & Contact Information Panel */}
        <Card className="glass-card border-2 border-indigo-100 shadow-lg">
          <CardHeader className="bg-gradient-to-r from-indigo-50 to-blue-50 pb-4">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <CardTitle className="text-3xl font-bold text-slate-900 mb-2">
                  {lead.raw_data?.name || "Unknown Lead"}
                </CardTitle>
                <div className="flex items-center gap-3 text-slate-600">
                  <div className="flex items-center gap-2">
                    <Building2 className="w-4 h-4" />
                    <span className="font-medium">{lead.raw_data?.company || "No company"}</span>
                  </div>
                  {lead.raw_data?.title && (
                    <>
                      <span className="text-slate-300">•</span>
                      <div className="flex items-center gap-2">
                        <Briefcase className="w-4 h-4" />
                        <span>{lead.raw_data.title}</span>
                      </div>
                    </>
                  )}
                </div>
              </div>
              
              {/* Enrichment Status */}
              <div className="flex items-center gap-2">
                {lead.enrichment_status === 'enriched' ? (
                  <div className="flex items-center gap-2">
                    <Badge className="bg-green-100 text-green-700 px-4 py-2">
                      <Sparkles className="w-4 h-4 mr-2" />
                      Enriched
                    </Badge>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={triggerReEnrichment}
                      title={`Re-enrich (${lead.enrichment_count || 0} times enriched)`}
                    >
                      <RefreshCw className="w-4 h-4" />
                    </Button>
                  </div>
                ) : lead.enrichment_status === 'processing' ? (
                  <Badge className="bg-blue-100 text-blue-700 px-4 py-2 animate-pulse">
                    Processing...
                  </Badge>
                ) : (
                  <Button onClick={triggerEnrichment}>
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Enrich Lead
                  </Button>
                )}
              </div>
            </div>
          </CardHeader>

          <CardContent className="pt-6">
            {/* Primary Contact Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              {/* Email */}
              <div className="p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Mail className="w-5 h-5 text-indigo-600" />
                    <span className="text-sm font-medium text-slate-500">Email</span>
                    {getVerificationIcon(lead.verify_email)}
                  </div>
                  <div className="flex items-center gap-1">
                    {isAIPopulated('email') && (
                      <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700 border-purple-200" title={`AI-suggested on ${getAIPopulatedDate('email')}`}>
                        <Bot className="w-3 h-3 mr-1" />
                        AI
                      </Badge>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => copyToClipboard(lead.raw_data?.email || '', 'Email')}
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                {lead.raw_data?.email ? (
                  <a href={`mailto:${lead.raw_data.email}`} className="text-slate-900 font-medium hover:text-indigo-600 transition-colors break-all">
                    {lead.raw_data.email}
                  </a>
                ) : (
                  <span className="text-slate-400 text-sm">No email available</span>
                )}
              </div>

              {/* Phone */}
              <div className="p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Phone className="w-5 h-5 text-indigo-600" />
                    <span className="text-sm font-medium text-slate-500">Phone</span>
                    {getVerificationIcon(lead.verify_phone)}
                  </div>
                  <div className="flex items-center gap-1">
                    {isAIPopulated('phone') && (
                      <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700 border-purple-200" title={`AI-suggested on ${getAIPopulatedDate('phone')}`}>
                        <Bot className="w-3 h-3 mr-1" />
                        AI
                      </Badge>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => copyToClipboard(lead.raw_data?.phone || '', 'Phone')}
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                {lead.raw_data?.phone ? (
                  <a href={`tel:${lead.raw_data.phone}`} className="text-slate-900 font-medium hover:text-indigo-600 transition-colors">
                    {lead.raw_data.phone}
                  </a>
                ) : (
                  <span className="text-slate-400 text-sm">No phone available</span>
                )}
              </div>
            </div>

            {/* Additional Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              {/* Lead Date */}
              {lead.lead_date && (
                <div className="p-4 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <Calendar className="w-5 h-5 text-indigo-600" />
                    <span className="text-sm font-medium text-slate-500">Lead Date</span>
                  </div>
                  <span className="text-slate-900 font-medium">
                    {new Date(lead.lead_date + 'T00:00:00').toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric'
                    })}
                  </span>
                </div>
              )}
            </div>

            {/* Stage Progression */}
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-slate-700 mb-3">Lead Stage</h3>
              <div className="relative grid grid-cols-6 gap-2" style={{ zIndex: 1 }}>
                <div className="absolute top-5 left-0 right-0 h-1 bg-slate-200 rounded-full" style={{ zIndex: 0 }}></div>
                <div 
                  className="absolute top-5 left-0 h-1 bg-gradient-to-r from-indigo-600 to-blue-600 rounded-full transition-all duration-500" 
                  style={{ 
                    width: `${(['new', 'contacted', 'qualified', 'negotiating', 'won', 'lost'].indexOf(lead.stage) / 5) * 100}%`,
                    zIndex: 0
                  }}
                ></div>

                {[
                  { name: 'New', value: 'new', icon: '🆕', color: 'blue' },
                  { name: 'Contacted', value: 'contacted', icon: '📞', color: 'cyan' },
                  { name: 'Qualified', value: 'qualified', icon: '✅', color: 'green' },
                  { name: 'Negotiating', value: 'negotiating', icon: '🤝', color: 'orange' },
                  { name: 'Won', value: 'won', icon: '🎉', color: 'emerald' },
                  { name: 'Lost', value: 'lost', icon: '❌', color: 'red' }
                ].map((stage) => (
                  <button
                    key={stage.value}
                    onClick={async () => {
                      try {
                        await axios.patch(`${API}/leads/${id}`, { stage: stage.value }, { withCredentials: true });
                        toast.success(`Stage updated to ${stage.name}`);
                        fetchLeadDetails();
                      } catch (error) {
                        toast.error("Failed to update stage");
                      }
                    }}
                    className={`flex flex-col items-center p-3 rounded-lg transition-all duration-200 group ${
                      lead.stage === stage.value
                        ? 'bg-indigo-100 border-2 border-indigo-500 shadow-md'
                        : 'bg-white border-2 border-slate-200 hover:border-indigo-300 hover:shadow-md'
                    }`}
                    style={{
                      backgroundColor: lead.stage === stage.value ? '#e0e7ff' : '#ffffff',
                      borderColor: lead.stage === stage.value ? '#6366f1' : '#e2e8f0',
                      zIndex: 1
                    }}
                  >
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center text-xl mb-2 transition-all ${
                      lead.stage === stage.value ? 'bg-indigo-200' : 'bg-slate-100 group-hover:bg-indigo-100'
                    }`}>
                      {stage.icon}
                    </div>
                    <span className={`text-xs font-medium transition-all ${
                      lead.stage === stage.value ? 'text-slate-900' : 'text-slate-600 group-hover:text-indigo-600'
                    }`}>
                      {stage.name}
                    </span>
                    {lead.stage === stage.value && (
                      <div className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-2 h-2 bg-indigo-600 rounded-full animate-pulse"></div>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Secondary Information */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t border-slate-200">
              <div>
                <p className="text-xs text-slate-500 mb-1">Lead Score</p>
                <div className="flex items-center gap-2">
                  <Award className="w-4 h-4 text-amber-500" />
                  <span className="font-semibold text-slate-900">{lead.score_ai || 0}/100</span>
                </div>
              </div>
              <div>
                <p className="text-xs text-slate-500 mb-1">Source</p>
                <span className="text-sm font-medium text-slate-900">{lead.source || 'Unknown'}</span>
              </div>
              <div>
                <p className="text-xs text-slate-500 mb-1">Owner</p>
                <span className="text-sm font-medium text-slate-900">{lead.owner_email || 'Unassigned'}</span>
              </div>
              <div>
                <p className="text-xs text-slate-500 mb-1">Created</p>
                <span className="text-sm text-slate-900">{new Date(lead.created_at).toLocaleDateString()}</span>
              </div>
            </div>

            {/* AI Enrichment Timestamp */}
            {lead.ai_enriched_at && (
              <div className="mt-4 pt-4 border-t border-slate-200">
                <div className="flex items-center gap-2 text-xs text-slate-500">
                  <Bot className="w-3 h-3" />
                  <span>Last enriched by AI on {new Date(lead.ai_enriched_at).toLocaleString()}</span>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Lead Notes */}
        <Card className="glass-card">
          <CardHeader>
            <CardTitle>Lead Notes</CardTitle>
          </CardHeader>
          <CardContent>
            <LeadNotesEditor leadId={id} initialNotes={lead.lead_notes} onSave={fetchLeadDetails} />
          </CardContent>
        </Card>

        {/* Comprehensive Company Analysis - Scrollable Sections (No Tabs) */}
        {enrichedData.company_overview && (
          <Card className="glass-card border-2 border-indigo-100">
            <CardHeader className="bg-gradient-to-r from-indigo-50 to-blue-50">
              <CardTitle className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-indigo-600" />
                Comprehensive Company Analysis
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-8 pt-6">
              {/* Overview Section */}
              {enrichedData.company_overview && (
                <div className="">
                  <div className="flex items-center gap-2 mb-4">
                    <Building2 className="w-5 h-5 text-indigo-600" />
                    <h3 className="text-xl font-bold text-slate-900">Company Overview</h3>
                  </div>
                  <div className="space-y-4">
                    <p className="text-slate-600 leading-relaxed">{enrichedData.company_overview.description}</p>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {enrichedData.company_overview.founded_year && enrichedData.company_overview.founded_year !== 'Unknown' && (
                        <div className="bg-slate-50 p-3 rounded-lg">
                          <p className="text-xs text-slate-500 mb-1">Founded</p>
                          <p className="font-semibold text-slate-900">{enrichedData.company_overview.founded_year}</p>
                        </div>
                      )}
                      {enrichedData.company_overview.company_size && enrichedData.company_overview.company_size !== 'Unknown' && (
                        <div className="bg-slate-50 p-3 rounded-lg">
                          <p className="text-xs text-slate-500 mb-1">Company Size</p>
                          <p className="font-semibold text-slate-900">
                            {typeof enrichedData.company_overview.company_size === 'object' 
                              ? (enrichedData.company_overview.company_size.employees_range || 
                                 enrichedData.company_overview.company_size.employees_exact || 
                                 'Unknown')
                              : enrichedData.company_overview.company_size}
                          </p>
                        </div>
                      )}
                      {enrichedData.company_overview.industry && enrichedData.company_overview.industry !== 'Unknown' && (
                        <div className="bg-slate-50 p-3 rounded-lg">
                          <p className="text-xs text-slate-500 mb-1">Industry</p>
                          <p className="font-semibold text-slate-900">{enrichedData.company_overview.industry}</p>
                        </div>
                      )}
                      {enrichedData.company_overview.headquarters && enrichedData.company_overview.headquarters !== 'Unknown' && (
                        <div className="bg-slate-50 p-3 rounded-lg">
                          <p className="text-xs text-slate-500 mb-1">Headquarters</p>
                          <p className="font-semibold text-slate-900 text-sm">
                            {typeof enrichedData.company_overview.headquarters === 'object' 
                              ? `${enrichedData.company_overview.headquarters.address || ''} ${enrichedData.company_overview.headquarters.city || ''}, ${enrichedData.company_overview.headquarters.state || ''} ${enrichedData.company_overview.headquarters.country || ''}`.trim().replace(/,\s*$/, '')
                              : enrichedData.company_overview.headquarters}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Business Model Section */}
              {enrichedData.business_model && (
                <div className="pt-6 border-t border-slate-200">
                  <div className="flex items-center gap-2 mb-4">
                    <TrendingUp className="w-5 h-5 text-indigo-600" />
                    <h3 className="text-xl font-bold text-slate-900">Business Model</h3>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {enrichedData.business_model.revenue_range && enrichedData.business_model.revenue_range !== 'Unknown' && (
                      <div className="bg-green-50 p-4 rounded-lg border border-green-100">
                        <div className="flex items-center gap-2 mb-1">
                          <DollarSign className="w-4 h-4 text-green-600" />
                          <p className="text-xs font-medium text-green-700">Revenue Range</p>
                        </div>
                        <p className="text-slate-900 font-semibold">{enrichedData.business_model.revenue_range}</p>
                      </div>
                    )}
                    {enrichedData.business_model.funding_stage && enrichedData.business_model.funding_stage !== 'Unknown' && (
                      <div className="bg-blue-50 p-4 rounded-lg border border-blue-100">
                        <div className="flex items-center gap-2 mb-1">
                          <Target className="w-4 h-4 text-blue-600" />
                          <p className="text-xs font-medium text-blue-700">Funding Stage</p>
                        </div>
                        <p className="text-slate-900 font-semibold">{enrichedData.business_model.funding_stage}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Products & Services */}
              {enrichedData.products_services && (
                <div className="pt-6 border-t border-slate-200">
                  <div className="flex items-center gap-2 mb-4">
                    <Lightbulb className="w-5 h-5 text-indigo-600" />
                    <h3 className="text-xl font-bold text-slate-900">Products & Services</h3>
                  </div>
                  {enrichedData.products_services.primary_offerings && enrichedData.products_services.primary_offerings.length > 0 && (
                    <div className="space-y-2">
                      {enrichedData.products_services.primary_offerings.map((offering, idx) => (
                        <div key={idx} className="flex items-start gap-2 p-3 bg-slate-50 rounded-lg">
                          <div className="w-2 h-2 bg-indigo-500 rounded-full mt-2"></div>
                          <p className="text-slate-700">{offering}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Team Leadership */}
              {enrichedData.team_leadership && enrichedData.team_leadership.key_executives && (
                <div className="pt-6 border-t border-slate-200">
                  <div className="flex items-center gap-2 mb-4">
                    <Users className="w-5 h-5 text-indigo-600" />
                    <h3 className="text-xl font-bold text-slate-900">Team & Leadership</h3>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {enrichedData.team_leadership.key_executives.map((exec, idx) => (
                      <div key={idx} className="p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors flex items-center justify-between">
                        <div>
                          <p className="font-semibold text-slate-900">{exec.name}</p>
                          <p className="text-sm text-slate-600">{exec.title}</p>
                        </div>
                        {/* Support both linkedin and linkedin_url */}
                        {((exec.linkedin_url || exec.linkedin) && 
                         (exec.linkedin_url || exec.linkedin) !== 'N/A' && 
                         (exec.linkedin_url || exec.linkedin) !== 'Unknown' && 
                         (exec.linkedin_url || exec.linkedin) !== 'Not Available' &&
                         ((exec.linkedin_url || exec.linkedin).startsWith('http://') || (exec.linkedin_url || exec.linkedin).startsWith('https://'))) && (
                          <a 
                            href={exec.linkedin_url || exec.linkedin} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="flex items-center gap-1 text-blue-600 hover:text-blue-700"
                            title="View LinkedIn Profile"
                          >
                            <Linkedin className="w-5 h-5" />
                            <ExternalLink className="w-3 h-3" />
                          </a>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Key Contacts */}
              {enrichedData.key_contacts && (
                <div className="pt-6 border-t border-slate-200">
                  <div className="flex items-center gap-2 mb-4">
                    <Mail className="w-5 h-5 text-indigo-600" />
                    <h3 className="text-xl font-bold text-slate-900">Key Contacts</h3>
                  </div>
                  
                  <div className="space-y-6">
                    {/* Email Addresses */}
                    {enrichedData.key_contacts.emails && enrichedData.key_contacts.emails.length > 0 && (
                      <div>
                        <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                          <Mail className="w-4 h-4" />
                          Email Addresses ({enrichedData.key_contacts.emails.length})
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {enrichedData.key_contacts.emails.map((emailObj, idx) => {
                            // Support both new schema (value) and old schema (email)
                            const emailValue = emailObj.value || emailObj.email;
                            const verification = emailObj.verification || (emailObj.verified ? 'official' : null);
                            
                            return emailValue && emailValue !== 'Unknown' && emailValue !== 'N/A' && (
                              <div key={idx} className="p-3 bg-blue-50 rounded-lg border border-blue-100 hover:bg-blue-100 transition-colors">
                                <div className="flex items-start justify-between mb-1">
                                  <Badge variant="outline" className="text-xs bg-blue-100 text-blue-700 border-blue-200">
                                    {emailObj.type || 'General'}
                                  </Badge>
                                  {(emailObj.verified || verification === 'official') && (
                                    <CheckCircle2 className="w-3 h-3 text-green-600" />
                                  )}
                                </div>
                                <a 
                                  href={`mailto:${emailValue}`} 
                                  className="text-blue-600 hover:text-blue-700 font-medium text-sm break-all block mt-2"
                                >
                                  {emailValue}
                                </a>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Fallback for old format */}
                    {!enrichedData.key_contacts.emails && enrichedData.key_contacts.general_email && enrichedData.key_contacts.general_email !== 'N/A' && enrichedData.key_contacts.general_email !== 'Unknown' && (
                      <div>
                        <h4 className="text-sm font-semibold text-slate-700 mb-3">Email Addresses</h4>
                        <div className="p-4 bg-blue-50 rounded-lg border border-blue-100">
                          <p className="text-xs font-medium text-blue-700 mb-2">General Email</p>
                          <a href={`mailto:${enrichedData.key_contacts.general_email}`} className="text-blue-600 hover:text-blue-700 font-medium break-all">
                            {enrichedData.key_contacts.general_email}
                          </a>
                        </div>
                      </div>
                    )}

                    {/* Phone Numbers */}
                    {enrichedData.key_contacts.phones && enrichedData.key_contacts.phones.length > 0 && (
                      <div>
                        <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                          <Phone className="w-4 h-4" />
                          Phone Numbers ({enrichedData.key_contacts.phones.length})
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {enrichedData.key_contacts.phones.map((phoneObj, idx) => {
                            // Support both new schema (value) and old schema (phone)
                            const phoneValue = phoneObj.value || phoneObj.phone;
                            const verification = phoneObj.verification || (phoneObj.verified ? 'official' : null);
                            
                            return phoneValue && phoneValue !== 'Unknown' && phoneValue !== 'N/A' && (
                              <div key={idx} className="p-3 bg-green-50 rounded-lg border border-green-100 hover:bg-green-100 transition-colors">
                                <div className="flex items-start justify-between mb-1">
                                  <Badge variant="outline" className="text-xs bg-green-100 text-green-700 border-green-200">
                                    {phoneObj.type || 'Main'}
                                  </Badge>
                                  {(phoneObj.verified || verification === 'official') && (
                                    <CheckCircle2 className="w-3 h-3 text-green-600" />
                                  )}
                                </div>
                                <a 
                                  href={`tel:${phoneValue}`} 
                                  className="text-green-600 hover:text-green-700 font-medium text-sm block mt-2"
                                >
                                  {phoneValue}
                                </a>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Fallback for old format */}
                    {!enrichedData.key_contacts.phones && enrichedData.key_contacts.main_phone && enrichedData.key_contacts.main_phone !== 'N/A' && enrichedData.key_contacts.main_phone !== 'Unknown' && (
                      <div>
                        <h4 className="text-sm font-semibold text-slate-700 mb-3">Phone Numbers</h4>
                        <div className="p-4 bg-green-50 rounded-lg border border-green-100">
                          <p className="text-xs font-medium text-green-700 mb-2">Main Phone</p>
                          <a href={`tel:${enrichedData.key_contacts.main_phone}`} className="text-green-600 hover:text-green-700 font-medium">
                            {enrichedData.key_contacts.main_phone}
                          </a>
                        </div>
                      </div>
                    )}

                    {/* Other Contacts */}
                    {enrichedData.key_contacts.other_contacts && enrichedData.key_contacts.other_contacts.length > 0 && (
                      <div>
                        <h4 className="text-sm font-semibold text-slate-700 mb-3">Other Contacts</h4>
                        <div className="space-y-2">
                          {enrichedData.key_contacts.other_contacts.map((contact, idx) => (
                            <div key={idx} className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                              <div className="flex items-center justify-between mb-2">
                                <div>
                                  <p className="font-medium text-slate-900">{contact.name}</p>
                                  <p className="text-xs text-slate-600">{contact.role}</p>
                                </div>
                                {(contact.linkedin_url || contact.linkedin) && (contact.linkedin_url || contact.linkedin) !== 'Unknown' && (contact.linkedin_url || contact.linkedin) !== 'N/A' && ((contact.linkedin_url || contact.linkedin).startsWith('http://') || (contact.linkedin_url || contact.linkedin).startsWith('https://')) && (
                                  <a href={contact.linkedin_url || contact.linkedin} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-700">
                                    <Linkedin className="w-4 h-4" />
                                  </a>
                                )}
                              </div>
                              <div className="flex flex-wrap gap-2 text-xs">
                                {contact.email && contact.email !== 'Unknown' && contact.email !== 'N/A' && (
                                  <a href={`mailto:${contact.email}`} className="text-blue-600 hover:text-blue-700">
                                    {contact.email}
                                  </a>
                                )}
                                {contact.phone && contact.phone !== 'Unknown' && contact.phone !== 'N/A' && (
                                  <a href={`tel:${contact.phone}`} className="text-green-600 hover:text-green-700">
                                    {contact.phone}
                                  </a>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Market Presence */}
              {enrichedData.market_presence && (
                <div className="pt-6 border-t border-slate-200">
                  <div className="flex items-center gap-2 mb-4">
                    <Network className="w-5 h-5 text-indigo-600" />
                    <h3 className="text-xl font-bold text-slate-900">Market Presence</h3>
                  </div>
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {enrichedData.market_presence.geographic_presence && enrichedData.market_presence.geographic_presence !== 'Unknown' && (
                        <div className="bg-slate-50 p-4 rounded-lg">
                          <div className="flex items-center gap-2 mb-1">
                            <Globe className="w-4 h-4 text-indigo-600" />
                            <p className="text-xs font-medium text-slate-500">Geographic Presence</p>
                          </div>
                          {Array.isArray(enrichedData.market_presence.geographic_presence) ? (
                            <div className="flex flex-wrap gap-2">
                              {enrichedData.market_presence.geographic_presence.map((location, idx) => (
                                <span key={idx} className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-700">
                                  {location}
                                </span>
                              ))}
                            </div>
                          ) : (
                            <p className="text-slate-900 font-semibold">{enrichedData.market_presence.geographic_presence}</p>
                          )}
                        </div>
                      )}
                      {enrichedData.market_presence.market_position && enrichedData.market_presence.market_position !== 'Unknown' && (
                        <div className="bg-slate-50 p-4 rounded-lg">
                          <div className="flex items-center gap-2 mb-1">
                            <Award className="w-4 h-4 text-indigo-600" />
                            <p className="text-xs font-medium text-slate-500">Market Position</p>
                          </div>
                          <p className="text-slate-900 font-semibold">{enrichedData.market_presence.market_position}</p>
                        </div>
                      )}
                      {enrichedData.market_presence.customer_base && enrichedData.market_presence.customer_base !== 'Unknown' && (
                        <div className="bg-slate-50 p-4 rounded-lg">
                          <div className="flex items-center gap-2 mb-1">
                            <Users className="w-4 h-4 text-indigo-600" />
                            <p className="text-xs font-medium text-slate-500">Customer Base</p>
                          </div>
                          <p className="text-slate-900 font-semibold">{enrichedData.market_presence.customer_base}</p>
                        </div>
                      )}
                    </div>
                    <div className="flex gap-3">
                      {enrichedData.market_presence.linkedin_url && enrichedData.market_presence.linkedin_url !== 'Unknown' && (enrichedData.market_presence.linkedin_url.startsWith('http://') || enrichedData.market_presence.linkedin_url.startsWith('https://')) && (
                        <a href={enrichedData.market_presence.linkedin_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors">
                          <Linkedin className="w-4 h-4" />
                          <span className="text-sm font-medium">LinkedIn</span>
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      )}
                      {enrichedData.market_presence.twitter_url && enrichedData.market_presence.twitter_url !== 'N/A' && enrichedData.market_presence.twitter_url !== 'Unknown' && (enrichedData.market_presence.twitter_url.startsWith('http://') || enrichedData.market_presence.twitter_url.startsWith('https://')) && (
                        <a href={enrichedData.market_presence.twitter_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-4 py-2 bg-sky-50 text-sky-700 rounded-lg hover:bg-sky-100 transition-colors">
                          <Twitter className="w-4 h-4" />
                          <span className="text-sm font-medium">Twitter</span>
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Technology Stack */}
              {enrichedData.technology_stack && (
                <div className="pt-6 border-t border-slate-200">
                  <div className="flex items-center gap-2 mb-4">
                    <Code className="w-5 h-5 text-indigo-600" />
                    <h3 className="text-xl font-bold text-slate-900">Technology Stack</h3>
                  </div>
                  <div className="space-y-4">
                    {enrichedData.technology_stack.tech_focus && enrichedData.technology_stack.tech_focus !== 'Unknown' && (
                      <div className="bg-purple-50 p-4 rounded-lg border border-purple-100">
                        <p className="text-xs font-medium text-purple-700 mb-2">Tech Focus</p>
                        {Array.isArray(enrichedData.technology_stack.tech_focus) ? (
                          <div className="flex flex-wrap gap-2">
                            {enrichedData.technology_stack.tech_focus.map((tech, idx) => (
                              <span key={idx} className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                                {tech}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <p className="text-slate-900">{enrichedData.technology_stack.tech_focus}</p>
                        )}
                      </div>
                    )}
                    {enrichedData.technology_stack.platforms && enrichedData.technology_stack.platforms.length > 0 && enrichedData.technology_stack.platforms[0] !== 'Unknown' && (
                      <div>
                        <p className="text-sm font-medium text-slate-700 mb-2">Platforms</p>
                        <div className="flex flex-wrap gap-2">
                          {enrichedData.technology_stack.platforms.map((platform, idx) => (
                            <Badge key={idx} variant="outline" className="bg-slate-50">
                              {platform}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                    {enrichedData.technology_stack.integrations && enrichedData.technology_stack.integrations.length > 0 && enrichedData.technology_stack.integrations[0] !== 'Unknown' && (
                      <div>
                        <p className="text-sm font-medium text-slate-700 mb-2">Integrations</p>
                        <div className="flex flex-wrap gap-2">
                          {enrichedData.technology_stack.integrations.map((integration, idx) => (
                            <Badge key={idx} variant="outline" className="bg-indigo-50 text-indigo-700">
                              {integration}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Recent Activity */}
              {enrichedData.recent_activity && (
                <div className="pt-6 border-t border-slate-200">
                  <div className="flex items-center gap-2 mb-4">
                    <Newspaper className="w-5 h-5 text-indigo-600" />
                    <h3 className="text-xl font-bold text-slate-900">Recent Activity</h3>
                  </div>
                  <div className="space-y-3">
                    {enrichedData.recent_activity.latest_news && enrichedData.recent_activity.latest_news !== 'Unknown' && (
                      <div className="p-4 bg-amber-50 rounded-lg border border-amber-100">
                        <p className="text-xs font-medium text-amber-700 mb-2">Latest News</p>
                        {Array.isArray(enrichedData.recent_activity.latest_news) ? (
                          <ul className="space-y-2">
                            {enrichedData.recent_activity.latest_news.map((news, idx) => (
                              <li key={idx} className="flex items-start gap-2">
                                <span className="w-1.5 h-1.5 bg-amber-600 rounded-full mt-2 flex-shrink-0"></span>
                                <p className="text-slate-900 text-sm">{news}</p>
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <p className="text-slate-900">{enrichedData.recent_activity.latest_news}</p>
                        )}
                      </div>
                    )}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {enrichedData.recent_activity.recent_funding && enrichedData.recent_activity.recent_funding !== 'N/A' && enrichedData.recent_activity.recent_funding !== 'Unknown' && (
                        <div className="bg-green-50 p-4 rounded-lg border border-green-100">
                          <p className="text-xs font-medium text-green-700 mb-2">Recent Funding</p>
                          {Array.isArray(enrichedData.recent_activity.recent_funding) ? (
                            <ul className="space-y-1">
                              {enrichedData.recent_activity.recent_funding.map((funding, idx) => (
                                <li key={idx} className="text-slate-900 text-sm font-semibold">{funding}</li>
                              ))}
                            </ul>
                          ) : (
                            <p className="text-slate-900 font-semibold">{enrichedData.recent_activity.recent_funding}</p>
                          )}
                        </div>
                      )}
                      {enrichedData.recent_activity.hiring_trends && enrichedData.recent_activity.hiring_trends !== 'Unknown' && (
                        <div className="bg-blue-50 p-4 rounded-lg border border-blue-100">
                          <p className="text-xs font-medium text-blue-700 mb-2">Hiring Trends</p>
                          <p className="text-slate-900 font-semibold">{enrichedData.recent_activity.hiring_trends}</p>
                        </div>
                      )}
                    </div>
                    {enrichedData.recent_activity.expansion_plans && enrichedData.recent_activity.expansion_plans !== 'Unknown' && (
                      <div className="p-4 bg-slate-50 rounded-lg">
                        <p className="text-xs font-medium text-slate-700 mb-2">Expansion Plans</p>
                        {Array.isArray(enrichedData.recent_activity.expansion_plans) ? (
                          <ul className="space-y-2">
                            {enrichedData.recent_activity.expansion_plans.map((plan, idx) => (
                              <li key={idx} className="flex items-start gap-2">
                                <span className="w-1.5 h-1.5 bg-slate-600 rounded-full mt-2 flex-shrink-0"></span>
                                <p className="text-slate-900 text-sm">{plan}</p>
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <p className="text-slate-900">{enrichedData.recent_activity.expansion_plans}</p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Lead Qualification */}
              {enrichedData.lead_qualification && (
                <div className="pt-6 border-t border-slate-200">
                  <div className="flex items-center gap-2 mb-4">
                    <CheckSquare className="w-5 h-5 text-indigo-600" />
                    <h3 className="text-xl font-bold text-slate-900">Lead Qualification & Approach</h3>
                  </div>
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="bg-indigo-50 p-4 rounded-lg border border-indigo-200">
                        <p className="text-xs font-medium text-indigo-700 mb-2">Qualification Score</p>
                        <div className="flex items-center gap-2">
                          <div className="text-3xl font-bold text-indigo-600">{enrichedData.lead_qualification.qualification_score}/100</div>
                        </div>
                      </div>
                      {enrichedData.lead_qualification.fit_assessment && enrichedData.lead_qualification.fit_assessment !== 'Unknown' && (
                        <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                          <p className="text-xs font-medium text-slate-700 mb-2">Fit Assessment</p>
                          <p className="text-slate-900 font-semibold">{enrichedData.lead_qualification.fit_assessment}</p>
                        </div>
                      )}
                    </div>
                    {/* 2x2 Grid Layout for Qualification Details */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Key Opportunities */}
                      {enrichedData.lead_qualification.key_opportunities && enrichedData.lead_qualification.key_opportunities.length > 0 && (
                        <div className="bg-green-50 p-4 rounded-lg border border-green-100">
                          <p className="text-sm font-semibold text-green-900 mb-3">Key Opportunities</p>
                          <ul className="space-y-2">
                            {enrichedData.lead_qualification.key_opportunities.map((opp, idx) => (
                              <li key={idx} className="flex items-start gap-2 text-sm text-green-800">
                                <span className="text-green-600 mt-0.5">✓</span>
                                <span>{opp}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* Potential Challenges */}
                      {enrichedData.lead_qualification.potential_challenges && enrichedData.lead_qualification.potential_challenges.length > 0 && (
                        <div className="bg-red-50 p-4 rounded-lg border border-red-100">
                          <p className="text-sm font-semibold text-red-900 mb-3">Potential Challenges</p>
                          <ul className="space-y-2">
                            {enrichedData.lead_qualification.potential_challenges.map((challenge, idx) => (
                              <li key={idx} className="flex items-start gap-2 text-sm text-red-800">
                                <span className="text-red-600 mt-0.5">⚠</span>
                                <span>{challenge}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* Recommended Approach */}
                      {enrichedData.lead_qualification.recommended_approach && enrichedData.lead_qualification.recommended_approach !== 'Unknown' && (
                        <div className="bg-blue-50 p-4 rounded-lg border border-blue-100">
                          <p className="text-sm font-semibold text-blue-900 mb-3">Recommended Approach</p>
                          <p className="text-blue-800 text-sm leading-relaxed">{enrichedData.lead_qualification.recommended_approach}</p>
                        </div>
                      )}

                      {/* Next Steps */}
                      {enrichedData.lead_qualification.next_steps && enrichedData.lead_qualification.next_steps.length > 0 && (
                        <div className="bg-purple-50 p-4 rounded-lg border border-purple-100">
                          <p className="text-sm font-semibold text-purple-900 mb-3">Next Steps</p>
                          <ol className="space-y-2">
                            {enrichedData.lead_qualification.next_steps.map((step, idx) => (
                              <li key={idx} className="flex items-start gap-2 text-sm text-purple-800">
                                <span className="text-purple-600 font-semibold min-w-[20px]">{idx + 1}.</span>
                                <span>{step}</span>
                              </li>
                            ))}
                          </ol>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Contact Insights */}
              {enrichedData.contact_insights && (
                <div className="pt-6 border-t border-slate-200">
                  <div className="flex items-center gap-2 mb-4">
                    <MessageSquare className="w-5 h-5 text-indigo-600" />
                    <h3 className="text-xl font-bold text-slate-900">Contact Insights & Strategy</h3>
                  </div>
                  <div className="space-y-4">
                    {enrichedData.contact_insights.contact_role_analysis && enrichedData.contact_insights.contact_role_analysis !== 'Unknown' && (
                      <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                        <p className="text-sm font-semibold text-slate-900 mb-2">Contact Role Analysis</p>
                        <p className="text-slate-700">{enrichedData.contact_insights.contact_role_analysis}</p>
                      </div>
                    )}
                    {enrichedData.contact_insights.likely_pain_points && enrichedData.contact_insights.likely_pain_points.length > 0 && enrichedData.contact_insights.likely_pain_points[0] !== 'Unknown' && (
                      <div className="bg-orange-50 p-4 rounded-lg border border-orange-100">
                        <p className="text-sm font-semibold text-orange-900 mb-2">Likely Pain Points</p>
                        <ul className="space-y-1">
                          {enrichedData.contact_insights.likely_pain_points.map((pain, idx) => (
                            <li key={idx} className="flex items-start gap-2 text-sm text-orange-800">
                              <span className="text-orange-600 mt-1">•</span>
                              <span>{pain}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {enrichedData.contact_insights.engagement_strategy && enrichedData.contact_insights.engagement_strategy !== 'Unknown' && (
                      <div className="bg-indigo-50 p-4 rounded-lg border border-indigo-100">
                        <p className="text-sm font-semibold text-indigo-900 mb-2">Engagement Strategy</p>
                        <p className="text-indigo-800">{enrichedData.contact_insights.engagement_strategy}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Tailored Insights (Company Profile-Based) */}
              <div className="pt-6 border-t border-slate-200">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <Target className="w-5 h-5 text-purple-600" />
                    <h3 className="text-xl font-bold text-slate-900">Tailored Insights & Strategy</h3>
                  </div>
                  <Button
                    onClick={generateTailoredInsights}
                    disabled={loadingInsights}
                    size="sm"
                    className="bg-purple-600 hover:bg-purple-700"
                  >
                    {loadingInsights ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Generating...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-4 h-4 mr-2" />
                        Generate AI Insights
                      </>
                    )}
                  </Button>
                </div>

                {tailoredInsights ? (
                  <div className="space-y-4">
                    {/* Fit Score */}
                    {tailoredInsights.fit_score !== undefined && (
                      <div className="bg-gradient-to-r from-purple-50 to-indigo-50 p-4 rounded-lg border border-purple-200">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Award className="w-5 h-5 text-purple-600" />
                            <p className="text-sm font-semibold text-purple-900">Fit Score</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-3xl font-bold text-purple-700">{tailoredInsights.fit_score}</span>
                            <span className="text-sm text-purple-600">/100</span>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Fit Analysis */}
                    {tailoredInsights.fit_analysis && (
                      <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                        <p className="text-sm font-semibold text-blue-900 mb-2 flex items-center gap-2">
                          <CheckCircle2 className="w-4 h-4" />
                          Fit Analysis
                        </p>
                        <p className="text-blue-800 text-sm">{tailoredInsights.fit_analysis}</p>
                      </div>
                    )}

                    {/* Recommended Approach */}
                    {tailoredInsights.recommended_approach && (
                      <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                        <p className="text-sm font-semibold text-green-900 mb-2 flex items-center gap-2">
                          <Lightbulb className="w-4 h-4" />
                          Recommended Approach
                        </p>
                        <p className="text-green-800 text-sm">{tailoredInsights.recommended_approach}</p>
                      </div>
                    )}

                    {/* Talking Points */}
                    {tailoredInsights.talking_points && tailoredInsights.talking_points.length > 0 && (
                      <div className="bg-indigo-50 p-4 rounded-lg border border-indigo-200">
                        <p className="text-sm font-semibold text-indigo-900 mb-2 flex items-center gap-2">
                          <MessageSquare className="w-4 h-4" />
                          Key Talking Points
                        </p>
                        <ul className="space-y-2">
                          {tailoredInsights.talking_points.map((point, idx) => (
                            <li key={idx} className="flex items-start gap-2 text-sm text-indigo-800">
                              <span className="text-indigo-600 mt-1">•</span>
                              <span>{point}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Potential Concerns */}
                    {tailoredInsights.potential_concerns && tailoredInsights.potential_concerns.length > 0 && (
                      <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
                        <p className="text-sm font-semibold text-amber-900 mb-2 flex items-center gap-2">
                          <XCircle className="w-4 h-4" />
                          Potential Concerns
                        </p>
                        <ul className="space-y-2">
                          {tailoredInsights.potential_concerns.map((concern, idx) => (
                            <li key={idx} className="flex items-start gap-2 text-sm text-amber-800">
                              <span className="text-amber-600 mt-1">⚠</span>
                              <span>{concern}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Next Actions */}
                    {tailoredInsights.next_actions && tailoredInsights.next_actions.length > 0 && (
                      <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                        <p className="text-sm font-semibold text-purple-900 mb-2 flex items-center gap-2">
                          <CheckSquare className="w-4 h-4" />
                          Recommended Next Actions
                        </p>
                        <ul className="space-y-2">
                          {tailoredInsights.next_actions.map((action, idx) => (
                            <li key={idx} className="flex items-start gap-2 text-sm text-purple-800">
                              <span className="text-purple-600 mt-1 font-bold">{idx + 1}.</span>
                              <span>{action}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="bg-slate-50 p-8 rounded-lg border border-slate-200 text-center">
                    <Target className="w-12 h-12 text-slate-400 mx-auto mb-3" />
                    <p className="text-slate-600 mb-2">No tailored insights generated yet</p>
                    <p className="text-sm text-slate-500">
                      Click "Generate AI Insights" to create personalized strategy recommendations based on your company profile
                    </p>
                  </div>
                )}
              </div>

              {/* Provenance & Data Quality (New 2-Call Schema) */}
              {enrichedData.provenance && (enrichedData.provenance.fields || enrichedData.provenance.unresolved_gaps) && (
                <div className="pt-6 border-t border-slate-200">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <Info className="w-5 h-5 text-indigo-600" />
                      <h3 className="text-xl font-bold text-slate-900">Data Provenance</h3>
                      <Badge variant="outline" className="text-xs bg-indigo-50 text-indigo-700 border-indigo-200">
                        Enhanced 2-Call Enrichment
                      </Badge>
                    </div>
                    <Button
                      onClick={() => setShowProvenance(!showProvenance)}
                      variant="outline"
                      size="sm"
                      className="flex items-center gap-2"
                    >
                      {showProvenance ? (
                        <>
                          <ChevronUp className="w-4 h-4" />
                          Hide Details
                        </>
                      ) : (
                        <>
                          <ChevronDown className="w-4 h-4" />
                          Show Details
                        </>
                      )}
                    </Button>
                  </div>
                  
                  {showProvenance && (
                    <div className="space-y-4 animate-in slide-in-from-top-2 duration-300">
                      {/* Source Tracking */}
                      {enrichedData.provenance.fields && enrichedData.provenance.fields.length > 0 && (
                        <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                          <p className="text-sm font-semibold text-slate-900 mb-3">Source Attribution (Top 5)</p>
                          <div className="space-y-2">
                            {enrichedData.provenance.fields.slice(0, 5).map((field, idx) => (
                              <div key={idx} className="p-2 bg-white rounded border border-slate-200 text-xs">
                                <div className="flex items-center justify-between mb-1">
                                  <span className="font-medium text-slate-700">{field.path}</span>
                                  <div className="flex items-center gap-2">
                                    <Badge variant="outline" className={`text-xs ${
                                      field.method === 'Observed' ? 'bg-green-50 text-green-700 border-green-200' : 
                                      field.method === 'Estimated' ? 'bg-yellow-50 text-yellow-700 border-yellow-200' : 
                                      'bg-blue-50 text-blue-700 border-blue-200'
                                    }`}>
                                      {field.method}
                                    </Badge>
                                    {field.confidence && (
                                      <span className="text-slate-500">
                                        {Math.round(field.confidence * 100)}% confident
                                      </span>
                                    )}
                                  </div>
                                </div>
                                {field.sources && field.sources.length > 0 && (
                                  <div className="flex flex-wrap gap-1 mt-1">
                                    {field.sources.map((source, sidx) => (
                                      <a
                                        key={sidx}
                                        href={source.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-700"
                                      >
                                        <span className={`px-1.5 py-0.5 rounded text-xs ${
                                          source.tier === 'Tier 1' ? 'bg-green-100 text-green-700' :
                                          source.tier === 'Tier 2' ? 'bg-blue-100 text-blue-700' :
                                          'bg-slate-100 text-slate-700'
                                        }`}>
                                          {source.tier}
                                        </span>
                                        <ExternalLink className="w-3 h-3" />
                                      </a>
                                    ))}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Unresolved Gaps */}
                      {enrichedData.provenance.unresolved_gaps && enrichedData.provenance.unresolved_gaps.length > 0 && (
                        <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
                          <p className="text-sm font-semibold text-amber-900 mb-2">Data Gaps Identified</p>
                          <div className="flex flex-wrap gap-2">
                            {enrichedData.provenance.unresolved_gaps.map((gap, idx) => (
                              <span key={idx} className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                                {gap}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Meta Information (New 2-Call Schema) */}
              {enrichedData.meta && (
                <div className="pt-6 border-t border-slate-200">
                  <div className="flex items-center gap-2 mb-4">
                    <Info className="w-5 h-5 text-indigo-600" />
                    <h3 className="text-xl font-bold text-slate-900">Enrichment Metadata</h3>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {enrichedData.meta.canonical_name && (
                      <div className="bg-slate-50 p-3 rounded-lg">
                        <p className="text-xs text-slate-500 mb-1">Canonical Name</p>
                        <p className="font-semibold text-slate-900">{enrichedData.meta.canonical_name}</p>
                      </div>
                    )}
                    {enrichedData.meta.confidence_overall !== undefined && enrichedData.meta.confidence_overall !== null && (
                      <div className="bg-slate-50 p-3 rounded-lg">
                        <p className="text-xs text-slate-500 mb-1">Overall Confidence</p>
                        <div className="flex items-center gap-2">
                          <div className="flex-1 bg-slate-200 rounded-full h-2">
                            <div 
                              className="bg-indigo-600 h-2 rounded-full"
                              style={{width: `${enrichedData.meta.confidence_overall * 100}%`}}
                            ></div>
                          </div>
                          <span className="font-semibold text-slate-900 text-sm">
                            {Math.round(enrichedData.meta.confidence_overall * 100)}%
                          </span>
                        </div>
                      </div>
                    )}
                    {enrichedData.meta.hq_city && enrichedData.meta.hq_country && (
                      <div className="bg-slate-50 p-3 rounded-lg">
                        <p className="text-xs text-slate-500 mb-1">HQ Location</p>
                        <p className="font-semibold text-slate-900">{enrichedData.meta.hq_city}, {enrichedData.meta.hq_country}</p>
                      </div>
                    )}
                    {enrichedData.meta.generated_at && (
                      <div className="bg-slate-50 p-3 rounded-lg">
                        <p className="text-xs text-slate-500 mb-1">Enriched At</p>
                        <p className="font-semibold text-slate-900 text-sm">
                          {new Date(enrichedData.meta.generated_at).toLocaleString()}
                        </p>
                      </div>
                    )}
                    {enrichedData.meta.known_domains && enrichedData.meta.known_domains.length > 0 && (
                      <div className="bg-slate-50 p-3 rounded-lg md:col-span-2">
                        <p className="text-xs text-slate-500 mb-2">Known Domains</p>
                        <div className="flex flex-wrap gap-2">
                          {enrichedData.meta.known_domains.map((domain, idx) => (
                            <span key={idx} className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-700">
                              {domain}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Tasks, Activity, Files */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <TasksListSection leadId={id} refreshTrigger={taskRefresh} />
          <ActivityLogsSection leadId={id} />
        </div>
        
        <FileUploadSection leadId={id} />
      </div>

      {showEditDialog && (
        <EditLeadDialog
          open={showEditDialog}
          lead={lead}
          onClose={() => setShowEditDialog(false)}
          onSuccess={() => {
            setShowEditDialog(false);
            fetchLeadDetails();
          }}
          onUpdated={fetchLeadDetails}
        />
      )}

      {showDuplicatesDialog && (
        <DuplicateLeadsDialog
          open={showDuplicatesDialog}
          onClose={() => setShowDuplicatesDialog(false)}
          leadId={id}
          leadName={lead?.raw_data?.name || "Unknown"}
        />
      )}

      {showEmailDialog && (
        <SendEmailDialog
          open={showEmailDialog}
          onClose={() => {
            setShowEmailDialog(false);
            fetchLeadDetails(); // Refresh to show new activity log
          }}
          leadId={id}
          leadEmail={lead?.email || lead?.raw_data?.email}
          leadName={lead?.raw_data?.name || lead?.name}
          leadCompany={lead?.company || lead?.raw_data?.company}
          leadPhone={lead?.phone || lead?.raw_data?.phone}
        />
      )}

      {showIntroEmailDialog && (
        <IntroEmailComposer
          open={showIntroEmailDialog}
          onClose={() => setShowIntroEmailDialog(false)}
          leadId={id}
          leadName={lead?.raw_data?.name || lead?.name || "Unknown"}
          leadEmail={lead?.email || lead?.raw_data?.email}
        />
      )}
    </div>
  );
};

export default LeadDetailsNew;
